import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface VideoSlide {
  image: string;
  caption: string;
}

interface FeaturedVideo {
  id: number;
  title: string;
  category: string;
  duration: string;
  views: string;
  thumbnail: string;
  description: string;
  slides: VideoSlide[];
}

const featuredVideos: FeaturedVideo[] = [
  {
    id: 1,
    title: 'Cocktail Fundamentals Masterclass',
    category: 'Beginner',
    duration: '3:45',
    views: '2.4K',
    thumbnail: 'https://public.readdy.ai/ai/img_res/4a3ec43592eef5775d55b6108a802f39.jpg',
    description: 'Learn the essential techniques every home bartender needs.',
    slides: [
      { image: 'https://readdy.ai/api/search-image?query=bartender%20demonstrating%20proper%20cocktail%20shaking%20technique%20with%20silver%20shaker%20warm%20lighting%20elegant%20bar%20setting%20professional%20mixology%20class&width=600&height=340&seq=slide-fund-1&orientation=landscape', caption: 'Shaking Technique' },
      { image: 'https://readdy.ai/api/search-image?query=close%20up%20hands%20measuring%20spirits%20with%20jigger%20precision%20pouring%20into%20mixing%20glass%20professional%20bartending%20fundamentals%20warm%20amber%20lighting&width=600&height=340&seq=slide-fund-2&orientation=landscape', caption: 'Precise Measuring' },
      { image: 'https://readdy.ai/api/search-image?query=bartender%20straining%20cocktail%20through%20hawthorne%20strainer%20into%20elegant%20coupe%20glass%20golden%20liquid%20professional%20technique%20demonstration&width=600&height=340&seq=slide-fund-3&orientation=landscape', caption: 'Perfect Straining' },
      { image: 'https://readdy.ai/api/search-image?query=beautiful%20finished%20cocktail%20with%20citrus%20garnish%20on%20bar%20counter%20with%20bar%20tools%20in%20background%20warm%20lighting%20professional%20presentation&width=600&height=340&seq=slide-fund-4&orientation=landscape', caption: 'Final Presentation' },
    ],
  },
  {
    id: 2,
    title: 'Advanced Mixology Techniques',
    category: 'Advanced',
    duration: '5:20',
    views: '1.8K',
    thumbnail: 'https://readdy.ai/api/search-image?query=advanced%20cocktail%20making%20class%20with%20molecular%20mixology%20techniques%2C%20smoke%20infusion%20demonstration%2C%20professional%20bartender%20creating%20artistic%20cocktail%2C%20sophisticated%20bar%20laboratory%20setting%2C%20dramatic%20lighting%20with%20amber%20tones%2C%20premium%20spirits%20and%20specialized%20equipment&width=600&height=340&seq=vid-adv-001&orientation=landscape',
    description: 'Explore advanced techniques including infusions and molecular mixology.',
    slides: [
      { image: 'https://readdy.ai/api/search-image?query=molecular%20mixology%20spherification%20technique%20creating%20caviar%20pearls%20dramatic%20lighting%20modern%20bar%20laboratory%20setting%20professional%20demonstration&width=600&height=340&seq=slide-adv-1&orientation=landscape', caption: 'Spherification' },
      { image: 'https://readdy.ai/api/search-image?query=cocktail%20smoke%20infusion%20with%20glass%20cloche%20dramatic%20smoke%20swirling%20around%20drink%20moody%20amber%20lighting%20sophisticated%20presentation&width=600&height=340&seq=slide-adv-2&orientation=landscape', caption: 'Smoke Infusion' },
      { image: 'https://readdy.ai/api/search-image?query=bartender%20creating%20foam%20topping%20for%20cocktail%20using%20nitrous%20dispenser%20modern%20mixology%20technique%20professional%20bar%20setting&width=600&height=340&seq=slide-adv-3&orientation=landscape', caption: 'Foam Artistry' },
      { image: 'https://readdy.ai/api/search-image?query=stunning%20molecular%20cocktail%20with%20floating%20bubbles%20and%20edible%20flowers%20artistic%20presentation%20dark%20moody%20background%20premium%20bar%20photography&width=600&height=340&seq=slide-adv-4&orientation=landscape', caption: 'Artistic Creation' },
    ],
  },
  {
    id: 3,
    title: 'Hen\'s Party Experience',
    category: 'Groups',
    duration: '4:15',
    views: '3.2K',
    thumbnail: 'https://readdy.ai/api/search-image?query=glamorous%20hens%20party%20bachelorette%20cocktail%20class%2C%20group%20of%20women%20making%20colorful%20drinks%20together%2C%20pink%20and%20gold%20decorations%2C%20champagne%20celebration%2C%20fun%20festive%20atmosphere%2C%20elegant%20bar%20setting%20with%20confetti%2C%20sophisticated%20ladies%20night%20experience&width=600&height=340&seq=vid-hens-001&orientation=landscape',
    description: 'The ultimate celebration experience for unforgettable parties.',
    slides: [
      { image: 'https://readdy.ai/api/search-image?query=group%20of%20women%20arriving%20at%20glamorous%20cocktail%20class%20pink%20decorations%20champagne%20welcome%20drinks%20elegant%20venue%20excited%20celebration%20atmosphere&width=600&height=340&seq=slide-hens-1&orientation=landscape', caption: 'Welcome Drinks' },
      { image: 'https://readdy.ai/api/search-image?query=bachelorette%20party%20learning%20to%20make%20pink%20cocktails%20together%20laughing%20having%20fun%20instructor%20demonstrating%20festive%20atmosphere&width=600&height=340&seq=slide-hens-2&orientation=landscape', caption: 'Learning Together' },
      { image: 'https://readdy.ai/api/search-image?query=women%20shaking%20cocktails%20at%20hens%20party%20class%20colorful%20drinks%20confetti%20celebration%20fun%20group%20activity%20elegant%20setting&width=600&height=340&seq=slide-hens-3&orientation=landscape', caption: 'Shake It Up' },
      { image: 'https://readdy.ai/api/search-image?query=glamorous%20group%20toast%20with%20colorful%20cocktails%20hens%20party%20celebration%20cheers%20moment%20pink%20gold%20decorations%20happy%20women&width=600&height=340&seq=slide-hens-4&orientation=landscape', caption: 'Cheers!' },
    ],
  },
  {
    id: 4,
    title: 'Whiskey Cocktail Classics',
    category: 'Intermediate',
    duration: '6:10',
    views: '1.9K',
    thumbnail: 'https://readdy.ai/api/search-image?query=whiskey%20cocktail%20masterclass%20demonstration%2C%20bartender%20crafting%20old%20fashioned%20and%20manhattan%2C%20premium%20bourbon%20and%20rye%20bottles%2C%20elegant%20dark%20wood%20bar%20setting%2C%20amber%20liquid%20being%20poured%20over%20ice%2C%20sophisticated%20moody%20lighting%2C%20classic%20cocktail%20preparation&width=600&height=340&seq=vid-whisk-001&orientation=landscape',
    description: 'Master the art of whiskey cocktails from Old Fashioned to Manhattan.',
    slides: [
      { image: 'https://readdy.ai/api/search-image?query=premium%20whiskey%20bottles%20lineup%20bourbon%20rye%20scotch%20on%20dark%20wood%20bar%20elegant%20amber%20lighting%20sophisticated%20tasting%20setup&width=600&height=340&seq=slide-whisk-1&orientation=landscape', caption: 'Premium Selection' },
      { image: 'https://readdy.ai/api/search-image?query=bartender%20muddling%20sugar%20and%20bitters%20for%20old%20fashioned%20cocktail%20close%20up%20hands%20technique%20dark%20moody%20bar%20setting&width=600&height=340&seq=slide-whisk-2&orientation=landscape', caption: 'Old Fashioned' },
      { image: 'https://readdy.ai/api/search-image?query=stirring%20manhattan%20cocktail%20in%20mixing%20glass%20with%20bar%20spoon%20ice%20cubes%20amber%20liquid%20elegant%20technique%20demonstration&width=600&height=340&seq=slide-whisk-3&orientation=landscape', caption: 'Manhattan Stir' },
      { image: 'https://readdy.ai/api/search-image?query=beautiful%20whiskey%20sour%20cocktail%20with%20egg%20white%20foam%20and%20cherry%20garnish%20elegant%20presentation%20dark%20wood%20background&width=600&height=340&seq=slide-whisk-4&orientation=landscape', caption: 'Whiskey Sour' },
    ],
  },
];

interface VideoCardProps {
  video: FeaturedVideo;
}

function VideoCard({ video }: VideoCardProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % video.slides.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [video.slides.length]);

  return (
    <div className="group bg-white/[0.03] rounded-2xl overflow-hidden border border-white/5 hover:border-amber-500/20 transition-all duration-500 hover:shadow-xl hover:shadow-amber-500/5">
      {/* Auto-playing Slideshow */}
      <div className="relative aspect-video overflow-hidden">
        {/* Slideshow Images */}
        <div className="absolute inset-0">
          {video.slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-700 ${
                currentSlide === index ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.caption}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/20 to-transparent"></div>
        
        {/* Slide Caption */}
        <div className="absolute top-3 right-3 px-3 py-1.5 bg-black/70 backdrop-blur-sm rounded-full text-white text-xs font-medium flex items-center gap-2">
          <i className="ri-slideshow-line text-amber-400"></i>
          {video.slides[currentSlide].caption}
        </div>

        {/* Slide Progress Dots */}
        <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
          {video.slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                currentSlide === index 
                  ? 'bg-amber-400 w-4' 
                  : 'bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>

        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 text-[10px] font-bold rounded-full uppercase tracking-wide">
            {video.category}
          </span>
        </div>

        {/* Duration Badge */}
        <div className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/60 backdrop-blur-sm rounded-full text-white/90 text-xs font-medium flex items-center gap-1">
          <i className="ri-time-line text-[10px]"></i>
          {video.duration}
        </div>

        {/* Auto-play Indicator */}
        <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-black/60 backdrop-blur-sm rounded-full text-white/90 text-xs font-medium flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"></span>
          Auto-playing
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-sm font-serif font-semibold text-white mb-1.5 group-hover:text-amber-300 transition-colors line-clamp-1">
          {video.title}
        </h3>
        <p className="text-white/40 text-xs leading-relaxed line-clamp-2 mb-3">
          {video.description}
        </p>
        <div className="flex items-center justify-between text-white/30 text-xs">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {video.views}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-image-2-line"></i>
            {video.slides.length} photos
          </span>
        </div>
      </div>
    </div>
  );
}

export default function FeaturedVideos() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });

  return (
    <section className="py-28 bg-stone-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>
      <div className="absolute top-40 -left-40 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-40 -right-40 w-80 h-80 bg-amber-400/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div 
          ref={headerRef}
          className={`flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-10 h-px bg-gradient-to-r from-transparent to-amber-500"></div>
              <span className="text-amber-400 text-xs font-semibold tracking-[0.25em] uppercase flex items-center gap-2">
                <i className="ri-slideshow-line"></i>
                Class Previews
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-normal text-white leading-tight">
              Featured <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300">Experiences</span>
            </h2>
            <p className="text-white/50 mt-4 max-w-xl leading-relaxed">
              Explore our collection of classes and events. Each slideshow automatically cycles through key moments.
            </p>
          </div>
          <Link
            to="/video-gallery"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/10 rounded-full text-white/80 hover:text-amber-300 hover:border-amber-500/30 transition-all cursor-pointer whitespace-nowrap group"
          >
            View All Galleries
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </Link>
        </div>

        {/* Video Grid */}
        <div 
          ref={gridRef}
          className={`grid md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children ${gridVisible ? 'visible' : ''}`}
        >
          {featuredVideos.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <p className="text-white/40 text-sm mb-6">
            Explore our complete collection of class galleries and event highlights
          </p>
          <Link
            to="/video-gallery"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-400 to-yellow-300 text-stone-900 font-semibold rounded-full hover:from-amber-300 hover:to-yellow-200 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-105"
          >
            <i className="ri-gallery-line text-xl"></i>
            Browse Full Gallery
          </Link>
        </div>
      </div>
    </section>
  );
}